import { createSuspenseQuery } from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

const urls = {
  wf: {
    resources: "/contact/:id/linked-resources",
    tasks: "/contact/:id/linked-tasks",
    users: "/contact/:id/linked-board-users",
    clients: "/contact/:id/linked-clients"
  },
  public: {
    resources: "/public/:slug/contact/:id/linked-resources",
    tasks: "/public/:slug/contact/:id/linked-tasks",
    users: "/public/:slug/contact/:id/linked-board-users",
    clients: "/public/:slug/contact/:id/linked-clients"
  }
} as const;

type EntityType = keyof (typeof urls)[keyof typeof urls];

const ResponseSchema = z.array(
  z.object({
    id: z.number(),
    name: z.string()
  })
);

type Response = z.infer<typeof ResponseSchema>;

const BaseRequestSchema = z.object({
  id: z.int().positive(),
  api: z.enum(["wf", "public"]),
  entity_type: z.enum(["resources", "tasks", "users", "clients"])
});
const RequestSchema = z.discriminatedUnion("api", [
  BaseRequestSchema.extend({
    api: z.literal("wf")
  }),
  BaseRequestSchema.extend({
    api: z.literal("public"),
    slug: z.string()
  })
]);

type Request = z.infer<typeof RequestSchema>;

const RequestVariables = z.union([
  BaseRequestSchema.extend({ api: z.literal("wf") }).omit({ api: true }),
  BaseRequestSchema.extend({ api: z.literal("public"), slug: z.string() }).omit(
    { api: true }
  )
]);

export const useContactEntitiesSuspense = (entity_type: EntityType) =>
  createSuspenseQuery<Response, Request, Error>({
    queryKey: [`contact${entity_type}EntitiesSuspense`],
    staleTime: 1000 * 60,
    fetcher: async (variables) => {
      const { api: apiType, ...rest } = variables;
      return api<Omit<Request, "api">, Response>({
        method: "GET",
        url: urls[apiType][entity_type],
        requestSchema: RequestVariables,
        responseSchema: ResponseSchema,
        body: rest
      });
    }
  });
